// WHO hears WHICH sensor event, with WHICH sound — 🔓 Unlocked and 🔒 Locked.
//
// One row per sensor event, one column per mode, each cell a people picker with
// checkboxes and a sound picker with ▶. Presets fill everything in one tap;
// "Same as 🔓" copies a column. Saved as a whole (SaveRfAlertRules replaces the
// location's sensors' rules). Model and presets: core/security/alertGrid.js;
// the server side: Home Automation api/new/security.js.
//
// Kept deliberately small: a cell is two dropdowns. Anything cleverer —
// per-room schedules, escalation — belongs elsewhere, not in this grid.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Select, Checkbox, ListItemText, Divider, Chip, LinearProgress, Table, TableBody, TableCell,
  TableHead, TableRow, useMediaQuery, useTheme, Tooltip,
} from '@mui/material';
import { useApp } from '../../core/state/AppContext';
import { getRfAlertRules, saveRfAlertRules } from '../../core/api/api';
import {
  gridRows, rulesToGrid, gridToRules, applyPreset, copyColumn, cellKey, rowModes, whoLabel, PRESETS,
} from '../../core/security/alertGrid';
import { soundOptions } from '../../core/push/pushChannels';
import SoundPlayButton from './SoundPlayButton';
import { fitDialog, PHONE_SHEET } from './fitDialog';

const SMALL = { fontSize: 12, '& .MuiSelect-select': { py: '4px', fontSize: 12 } };

function WhoSelect({ value, users, onChange }) {
  const ids = Array.isArray(value) ? value.map(String) : [];
  const selected = Array.isArray(value) ? ids : [String(value)];
  return (
    <Select
      multiple size="small" value={selected} sx={{ ...SMALL, width: 150 }}
      renderValue={() => whoLabel(value, users)}
      onChange={(e) => {
        const v = e.target.value;
        // The last thing ticked decides: a special choice replaces the list,
        // a person joins it.
        const last = v[v.length - 1];
        if (['default', 'all', 'nobody'].includes(last)) { onChange(last); return; }
        const people = v.filter((x) => !['default', 'all', 'nobody'].includes(x)).map(Number);
        onChange(people.length ? people : 'nobody');
      }}
    >
      <MenuItem value="default" dense><ListItemText primary="Default (as before)" secondary="this sensor's own settings" /></MenuItem>
      <MenuItem value="all" dense><Checkbox size="small" checked={value === 'all'} /><ListItemText primary="Everyone ticked" secondary="the sensor's Who is told list" /></MenuItem>
      <MenuItem value="nobody" dense><Checkbox size="small" checked={value === 'nobody'} /><ListItemText primary="Nobody" /></MenuItem>
      <Divider />
      {users.map((u) => (
        <MenuItem key={u.UserId} value={String(u.UserId)} dense>
          <Checkbox size="small" checked={ids.includes(String(u.UserId))} />
          <ListItemText primary={u.FullName || `User ${u.UserId}`} secondary={u.MobileNumber || null} />
        </MenuItem>
      ))}
    </Select>
  );
}

function Cell({ cell, users, onChange, disabled }) {
  const c = cell || { who: 'default', sound: '' };
  const silent = c.who === 'default' || c.who === 'nobody';
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <WhoSelect value={c.who} users={users} onChange={(who) => onChange({ ...c, who })} />
      <Select
        size="small" value={c.sound || ''} displayEmpty disabled={disabled || silent}
        sx={{ ...SMALL, width: 140 }}
        onChange={(e) => onChange({ ...c, sound: e.target.value })}
      >
        <MenuItem value="" dense>Event&apos;s own sound</MenuItem>
        {soundOptions(c.sound).map((s) => <MenuItem key={s.sound} value={s.sound} dense>{s.name}</MenuItem>)}
      </Select>
      {!silent && c.sound && <SoundPlayButton sound={c.sound} sx={{ p: 0.25 }} />}
    </Box>
  );
}

/**
 * @param open, onClose
 * @param locationId   whose sensors
 * @param locationName for the title
 */
export default function SecurityAlertsDialog({
  open, onClose, locationId, locationName = '',
}) {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const { userId } = useApp();
  const [data, setData] = useState(null);       // { sensors, rules, users }
  const [grid, setGrid] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open || !locationId) return;
    setData(null); setError(''); setSaved(false);
    getRfAlertRules(locationId, userId)
      .then((r) => {
        const d = r && r.data;
        if (!d || d.ok === false) throw new Error((d && d.error) || 'not available');
        setData(d);
        setGrid(rulesToGrid(d.rules || [], gridRows(d.sensors || [])));
      })
      .catch((e) => setError(e?.response?.status === 404
        ? 'The server does not have alert rules yet — deploy the backend and run security_migration.sql.'
        : `Could not load the alert rules (${e.message || 'error'}).`));
  }, [open, locationId, userId]);

  const rows = useMemo(() => gridRows(data ? data.sensors : []), [data]);
  const users = (data && data.users) || [];
  const set = (k, cell) => { setSaved(false); setGrid((g) => ({ ...g, [k]: cell })); };

  const save = async () => {
    setBusy(true); setError('');
    try {
      const ids = [...new Set(rows.map((r) => r.rfDeviceId))];
      const r = await saveRfAlertRules(locationId, userId, ids, gridToRules(grid, rows));
      if (r?.data?.ok === false) throw new Error(r.data.error);
      setSaved(true);
    } catch (e) {
      setError(`Could not save (${e.message || 'error'}). Nothing was changed.`);
    } finally {
      setBusy(false);
    }
  };

  // Group the rows by sensor for the table.
  const bySensor = [];
  rows.forEach((r) => {
    const last = bySensor[bySensor.length - 1];
    if (last && last.rfDeviceId === r.rfDeviceId) last.rows.push(r);
    else bySensor.push({ rfDeviceId: r.rfDeviceId, head: r, rows: [r] });
  });

  return (
    <Dialog open={!!open} onClose={busy ? undefined : onClose} {...(narrow ? PHONE_SHEET : fitDialog({}, 'md'))}>
      <DialogTitle sx={{ pb: 0.5 }}>
        {`Alerts${locationName ? ` — ${locationName}` : ''}`}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Who is told, and with which sound — when the premises are 🔓 unlocked and when they are 🔒 locked.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
        {!data && !error && <LinearProgress />}
        {data && !rows.length && (
          <Alert severity="info">No named sensors at this location yet — add one from RF Devices.</Alert>
        )}
        {data && rows.length > 0 && (
          <>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Quick set:</Typography>
              {PRESETS.map((p) => (
                <Tooltip key={p.key} title={p.hint}>
                  <Button size="small" variant="outlined" onClick={() => { setSaved(false); setGrid((g) => applyPreset(p.key, rows, g)); }}>
                    {p.label}
                  </Button>
                </Tooltip>
              ))}
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => { setSaved(false); setGrid((g) => copyColumn(g, rows)); }}>
                Same as 🔓 → 🔒
              </Button>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { px: 0.75, py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Sensor</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>🔓 Unlocked (someone home)</TableCell>
                    <TableCell>🔒 Locked</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bySensor.map((s) => s.rows.map((row, i) => (
                    <TableRow key={`${row.rfDeviceId}-${row.meaning}`}>
                      {i === 0 && (
                        <TableCell rowSpan={s.rows.length} sx={{ verticalAlign: 'top' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{`${s.head.icon} ${s.head.name}`}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.head.room || 'Whole location'}</Typography>
                        </TableCell>
                      )}
                      <TableCell><Typography variant="body2">{row.meaning}</Typography></TableCell>
                      {row.lifeSafety ? (
                        <TableCell colSpan={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Cell
                              cell={grid[cellKey(row.rfDeviceId, row.meaning, 'unlocked')]}
                              users={users}
                              onChange={(c) => set(cellKey(row.rfDeviceId, row.meaning, 'unlocked'), c)}
                            />
                            <Chip size="small" color="error" variant="outlined" label="always — life safety, both modes" />
                          </Box>
                        </TableCell>
                      ) : rowModes(row).map((mode) => (
                        <TableCell key={mode}>
                          <Cell
                            cell={grid[cellKey(row.rfDeviceId, row.meaning, mode)]}
                            users={users}
                            onChange={(c) => set(cellKey(row.rfDeviceId, row.meaning, mode), c)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  )))}
                </TableBody>
              </Table>
            </Box>
            <Typography variant="caption" color="text.secondary" className="fit-text" sx={{ display: 'block', mt: 1 }}>
              🚨 When the premises are LOCKED and nobody was inside (no motion 1–10 minutes after locking), any opening,
              motion or switch pressed by hand alerts EVERYONE loudly — whatever this grid says. Each person can still
              mute a sensor for themselves.
            </Typography>
          </>
        )}
        {saved && <Alert severity="success" sx={{ mt: 1 }}>Saved — the next event uses these settings.</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button variant="contained" onClick={save} disabled={busy || !data || !rows.length}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
